import React from 'react';
import { Globe, Heart, Shield, Target, Lightbulb, Database, MessageSquare, Star, DollarSign } from 'lucide-react';
import './AboutUs.css';

const AboutUs: React.FC = () => {
  return (
    <div className="about-us">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>About Us</h1>
          <p>Leading the future of global healthcare through innovation and excellence</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Company Introduction */}
        <section className="section">
          <div className="section-header">
            <Globe className="section-icon" />
            <h2>Company Introduction</h2>
          </div>
          <div className="company-intro">
            <div className="intro-image">
              <img 
                src="/taipeiims.jpg" 
                alt="Medical facility" 
              />
            </div>
            <div className="intro-text">
              <p>
                Global Medical Tourism is at the forefront of revolutionizing healthcare access worldwide,
                combining exceptional medical services with seamless travel experiences.
              </p>
            </div>
          </div>
        </section>

        {/* Mission Statement */}
        <section className="section">
          <div className="section-header">
            <Target className="section-icon" />
            <h2>Mission Statement</h2>
          </div>
          <div className="content-card">
            <p>
              At Global Medical Tourism, our mission is to be the world's leading provider of integrated 
              healthcare and travel solutions, delivering unparalleled access to top-tier medical services 
              and wellness experiences across international borders.
            </p>
            <p>
              Through strategic partnerships with globally accredited medical providers and advanced facilities, 
              we ensure the highest standards of care while transforming the healthcare journey into an 
              opportunity for rejuvenation and exploration.
            </p>
          </div>
        </section>

        {/* Business Philosophy */}
        <section className="section">
          <div className="section-header">
            <Lightbulb className="section-icon" />
            <h2>Business Philosophy</h2>
          </div>
          <div className="philosophy-grid">
            <div className="philosophy-card">
              <Heart className="card-icon" />
              <h3>Excellence in Care</h3>
              <p>
                We collaborate with internationally accredited healthcare providers and advanced medical 
                facilities, ensuring the highest standards of safety, expertise, and outcomes.
              </p>
            </div>
            <div className="philosophy-card">
              <Globe className="card-icon" />
              <h3>Integration of Health and Travel</h3>
              <p>
                By harmonizing medical treatment with cultural, leisure, and wellness experiences, 
                we create holistic solutions that enhance recovery and rejuvenation.
              </p>
            </div>
            <div className="philosophy-card">
              <Shield className="card-icon" />
              <h3>Ethics and Transparency</h3>
              <p>
                We prioritize trust and accountability, maintaining clear communication and fair 
                practices while adhering to global regulatory standards.
              </p>
            </div>
          </div>
        </section>

        {/* Medical Tourism in Taipei */}
        <section className="section medical-tourism">
          <div className="section-header">
            <Star className="section-icon" />
            <h2>Medical Tourism in Taipei</h2>
          </div>
          
          {/* Cost Comparison */}
          <div className="content-card">
            <h3>Cost-Effective Healthcare Solutions</h3>
            <div className="pricing-grid">
              <div className="pricing-card">
                <h3>Dental Implant</h3>
                <div className="price-comparison">
                  <span className="price taiwan">Taiwan: $1,200</span>
                  <span className="price us">US: $3,000-$5,000</span>
                </div>
                <p>Save up to 75% on dental procedures</p>
              </div>
              <div className="pricing-card">
                <h3>Rhinoplasty</h3>
                <div className="price-comparison">
                  <span className="price taiwan">Taiwan: $2,500-$4,000</span>
                  <span className="price us">US: $8,000-$12,000</span>
                </div>
                <p>Save up to 70% on cosmetic surgery</p>
              </div>
              <div className="pricing-card">
                <h3>IVF Treatment</h3>
                <div className="price-comparison">
                  <span className="price taiwan">Taiwan: $3,500-$6,000</span>
                  <span className="price us">US: $15,000+</span>
                </div>
                <p>Save up to 75% on fertility treatments</p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="benefits-grid">
            <div className="benefit-card">
              <h3>World-Class Healthcare</h3>
              <ul className="benefit-list">
                <li>JCI-accredited hospitals</li>
                <li>US and UK-trained physicians</li>
                <li>State-of-the-art medical equipment</li>
                <li>Advanced diagnostic technology</li>
              </ul>
            </div>
            <div className="benefit-card">
              <h3>Patient Support</h3>
              <ul className="benefit-list">
                <li>Multilingual medical staff</li>
                <li>International patient services</li>
                <li>Visa assistance</li>
                <li>Travel arrangements support</li>
              </ul>
            </div>
            <div className="benefit-card">
              <h3>Convenient Location</h3>
              <ul className="benefit-list">
                <li>Direct flights from major cities</li>
                <li>Efficient public transportation</li>
                <li>Safe and clean environment</li>
                <li>Rich cultural experiences</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Corporate Vision */}
        <section className="section">
          <div className="section-header">
            <Target className="section-icon" />
            <h2>Corporate Vision</h2>
          </div>
          <div className="content-card vision-intro">
            <p>
              Our vision at Global Medical Tourism is to be the global leader in transforming healthcare 
              access by seamlessly integrating world-class medical services with exceptional travel 
              experiences and cutting-edge technology.
            </p>
          </div>
          <div className="vision-grid">
            <div className="vision-card">
              <Database className="card-icon" />
              <h3>Medical AI Database</h3>
              <p>
                Establishing a robust, intelligent database that empowers patients and providers with 
                accurate, real-time information for personalized treatment plans.
              </p>
            </div>
            <div className="vision-card">
              <MessageSquare className="card-icon" />
              <h3>Smart Medical Interactive Platform</h3>
              <p>
                Developing an innovative platform that enables dynamic, interactive disease management 
                and healthcare solutions.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;